import { useState } from "react";
import { useAuth } from "../auth.js";
import { DesktopDownloadCta } from "../DesktopDownloadCta.js";
import { LocaleToggle, SHIKKA_LOGO, ThemeToggle, useLocale } from "../ui.js";
import { AssistantComposerPanel } from "../panels/AssistantComposerPanel.js";
import { OwnerConsole } from "../panels/OwnerConsole.js";
import type { TranslateKey } from "../i18n.js";

type Tab = "chat" | "owner";

const USER_TABS: { id: Tab; label: TranslateKey }[] = [{ id: "chat", label: "chat" }];

export function Dashboard() {
  const { account, token, isOwner, logout } = useAuth();
  const { tr } = useLocale();
  const tabs = isOwner ? ([...USER_TABS, { id: "owner" as Tab, label: "ownerConsole" as TranslateKey }] as const) : USER_TABS;
  const [tab, setTab] = useState<Tab>("chat");

  if (!account || !token) return null;

  const roleLabel = isOwner ? tr("owner") : tr("member");
  const activeLabel = tabs.find((t) => t.id === tab)?.label ?? tabs[0]!.label;

  return (
    <div className="ss-shell">
      <div className="ss-rail">
        <div className="ss-rail-logo">
          <img src={SHIKKA_LOGO} alt="Smart Shikka" />
        </div>
        <div className="ss-rail-spacer" />
        <div className="ss-rail-avatar">
          <img src={SHIKKA_LOGO} alt="account" />
        </div>
      </div>

      <aside className="ss-sidebar">
        <div className="ss-sidebar-head">
          <div className="eyebrow">{isOwner ? tr("ownerConsole") : tr("member")}</div>
          <h2>{tr("appName")}</h2>
          <p>{tr("tagline")}</p>
        </div>
        <nav className="ss-sidebar-group">
          {tabs.map((item) => (
            <button
              key={item.id}
              className={`ss-side-item ${tab === item.id ? "active" : ""}`}
              onClick={() => setTab(item.id)}
            >
              {tr(item.label)}
            </button>
          ))}
        </nav>
        <section className="ss-sidebar-download" aria-label={tr("desktopDownload")}>
          <div className="ss-sidebar-download-title">{tr("desktopDownload")}</div>
          <DesktopDownloadCta />
        </section>
      </aside>

      <main className="ss-main">
        <div className="ss-topbar">
          <div className="title">{tr(activeLabel)}</div>
          <div className="spacer" />
          <span className={`badge ${isOwner ? "owner" : "warn"}`}>{roleLabel}</span>
          <ThemeToggle />
          <LocaleToggle />
          <div className="ss-chip-user">
            <div className="ss-avatar">
              <img src={SHIKKA_LOGO} alt="account" />
            </div>
            <div>
              <div className="name">{account.email}</div>
              <div className="sub">{roleLabel}</div>
            </div>
            <button className="ss-btn-ghost" onClick={() => void logout()}>
              {tr("logout")}
            </button>
          </div>
        </div>
        <div className="ss-scroll">
          <div className="ss-canvas">
            {tab === "chat" ? <AssistantComposerPanel /> : null}
            {tab === "owner" && isOwner ? <OwnerConsole token={token} /> : null}
          </div>
        </div>
      </main>
    </div>
  );
}
